import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { ScoreDrilldownDialog, StartupCard, StartupCardSkeleton, SignalItem } from '../components/features/Cards';
import { Button } from '../components/ui/button';
import { dashboardApi, startupApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { readSessionJson, writeSessionJson } from '../lib/utils';
import {
  AlertCircle,
  TrendingUp,
  Briefcase,
  Mail,
  Eye,
  Plus,
  ArrowRight,
  Zap,
  RefreshCw
} from 'lucide-react';

const DASHBOARD_BRIEF_CACHE_KEY = 'agent-vc.dashboard.brief.v2';
const DASHBOARD_TOP_CACHE_KEY = 'agent-vc.dashboard.top.v2';
const EMPTY_BRIEF = {
  top_startups: [],
  recent_signals: [],
  stats: { watchlist_count: 0, deal_rooms_count: 0, pending_outreach: 0 },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [briefData, setBriefData] = useState(EMPTY_BRIEF);
  const [error, setError] = useState(null);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [scoreDrilldown, setScoreDrilldown] = useState(null);
  const [scoreDrilldownLoading, setScoreDrilldownLoading] = useState(false);
  const [scoreDrilldownError, setScoreDrilldownError] = useState(null);
  const [selectedStartup, setSelectedStartup] = useState(null);

  const fetchBrief = async () => {
    setLoading(true);
    const cachedBrief = readSessionJson(DASHBOARD_BRIEF_CACHE_KEY, EMPTY_BRIEF);
    const cachedTop = readSessionJson(DASHBOARD_TOP_CACHE_KEY, []);

    try {
      const [briefResult, topResult] = await Promise.allSettled([
        dashboardApi.getBrief(),
        startupApi.getTop(5),
      ]);

      const liveBrief = briefResult.status === 'fulfilled' ? briefResult.value.data : null;
      const liveTop = topResult.status === 'fulfilled' ? topResult.value.data.startups || [] : null;
      const resolvedBrief = liveBrief || cachedBrief || EMPTY_BRIEF;
      const resolvedTop = liveTop || (cachedTop.length > 0 ? cachedTop : resolvedBrief.top_startups || []);

      setBriefData({
        ...resolvedBrief,
        top_startups: resolvedTop,
      });

      if (liveBrief) {
        writeSessionJson(DASHBOARD_BRIEF_CACHE_KEY, liveBrief);
      }
      if (liveTop) {
        writeSessionJson(DASHBOARD_TOP_CACHE_KEY, liveTop);
      }

      if (briefResult.status === 'rejected' || topResult.status === 'rejected') {
        setError(resolvedTop.length > 0 || resolvedBrief.recent_signals?.length > 0
          ? 'Live data is still warming up. Showing the freshest cached research available.'
          : 'The API is responding slowly. Try refresh again in a moment.');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch brief:', err);
      setError(cachedTop.length > 0 || cachedBrief.recent_signals?.length > 0
        ? 'Live data is unavailable right now. Showing cached dashboard research.'
        : 'Failed to load dashboard data.');
      setBriefData({
        ...(cachedBrief || EMPTY_BRIEF),
        top_startups: cachedTop.length > 0 ? cachedTop : cachedBrief.top_startups || [],
      });
    } finally {
      setLoading(false);
    }
  };

  const openScoreDrilldown = async (startup) => {
    setSelectedStartup(startup);
    setScoreDialogOpen(true);
    setScoreDrilldown(null);
    setScoreDrilldownError(null);
    setScoreDrilldownLoading(true);

    try {
      const response = await startupApi.getScoreDrilldown(startup.startup_id);
      setScoreDrilldown(response.data);
    } catch (err) {
      console.error('Failed to fetch score drilldown:', err);
      setScoreDrilldownError('The persisted scoring trace could not be loaded.');
    } finally {
      setScoreDrilldownLoading(false);
    }
  };

  useEffect(() => {
    fetchBrief();
  }, []);

  const stats = briefData?.stats || {};

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50 mb-1">
              Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-zinc-400 font-['Manrope']">Your daily venture intelligence brief</p>
          </div>
          <Button
            onClick={fetchBrief}
            variant="outline"
            data-testid="refresh-btn"
            className="border-zinc-800 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-900"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-400/20 bg-amber-400/10 px-4 py-3">
            <AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-100">{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer hover:border-zinc-700 transition-colors"
            onClick={() => navigate('/discover')}
            data-testid="stat-discover"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-400/10 rounded-md flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-mono text-zinc-50">{briefData?.top_startups?.length || 0}</p>
                <p className="text-zinc-500 text-sm">Top Startups</p>
              </div>
            </div>
          </div>

          <div
            className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer hover:border-zinc-700 transition-colors"
            onClick={() => navigate('/deal-rooms')}
            data-testid="stat-deals"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-400/10 rounded-md flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-mono text-zinc-50">{stats.deal_rooms_count || 0}</p>
                <p className="text-zinc-500 text-sm">Active Deals</p>
              </div>
            </div>
          </div>

          <div
            className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer hover:border-zinc-700 transition-colors"
            onClick={() => navigate('/watchlist')}
            data-testid="stat-watchlist"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-400/10 rounded-md flex items-center justify-center">
                <Eye className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-mono text-zinc-50">{stats.watchlist_count || 0}</p>
                <p className="text-zinc-500 text-sm">Watchlist</p>
              </div>
            </div>
          </div>

          <div
            className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer hover:border-zinc-700 transition-colors"
            onClick={() => navigate('/outreach')}
            data-testid="stat-outreach"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-400/10 rounded-md flex items-center justify-center">
                <Mail className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <p className="text-2xl font-mono text-zinc-50">{stats.pending_outreach || 0}</p>
                <p className="text-zinc-500 text-sm">Pending Outreach</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-3 gap-6">
          {/* Top Startups */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Top Ranked Startups
              </h2>
              <Button
                variant="ghost"
                onClick={() => navigate('/discover')}
                data-testid="view-all-startups"
                className="text-zinc-400 hover:text-zinc-50 text-sm"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            <div className="space-y-3">
              {loading ? (
                [...Array(5)].map((_, i) => <StartupCardSkeleton key={i} />)
              ) : briefData?.top_startups?.length > 0 ? (
                briefData.top_startups.slice(0, 5).map((startup) => (
                  <StartupCard
                    key={startup.startup_id}
                    startup={startup}
                    onClick={() => navigate(`/startups/${startup.startup_id}`)}
                    onScoreClick={startup.score_drilldown_available ? openScoreDrilldown : undefined}
                  />
                ))
              ) : (
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-8 text-center">
                  <p className="text-zinc-400 font-['Manrope'] mb-4">No startups yet. Start by adding companies.</p>
                  <Button
                    onClick={() => navigate('/discover')}
                    data-testid="add-startup-btn"
                    className="bg-white text-zinc-900 hover:bg-zinc-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Startup
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Recent Signals */}
            <div>
              <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Recent Signals
              </h2>
              <div className="space-y-2">
                {loading ? (
                  [...Array(4)].map((_, i) => (
                    <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-md p-3 animate-pulse">
                      <div className="h-4 w-3/4 bg-zinc-800 rounded mb-2" />
                      <div className="h-3 w-1/2 bg-zinc-800 rounded" />
                    </div>
                  ))
                ) : briefData?.recent_signals?.length > 0 ? (
                  briefData.recent_signals.slice(0, 5).map((item, i) => (
                    <SignalItem
                      key={i}
                      signal={item.signal}
                      startupName={item.startup_name}
                    />
                  ))
                ) : (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 text-center">
                    <p className="text-zinc-500 text-sm font-['Manrope']">No signals yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/deal-rooms')}
                  variant="outline"
                  data-testid="quick-new-deal"
                  className="w-full justify-start border-zinc-800 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-900"
                >
                  <Briefcase className="w-4 h-4 mr-3" />
                  New Deal Room
                </Button>
                <Button
                  onClick={() => navigate('/theses')}
                  variant="outline"
                  data-testid="quick-new-thesis"
                  className="w-full justify-start border-zinc-800 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-900"
                >
                  <Zap className="w-4 h-4 mr-3" />
                  Create Thesis
                </Button>
                <Button
                  onClick={() => navigate('/outreach')}
                  variant="outline"
                  data-testid="quick-new-outreach"
                  className="w-full justify-start border-zinc-800 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-900"
                >
                  <Mail className="w-4 h-4 mr-3" />
                  Draft Outreach
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ScoreDrilldownDialog
          open={scoreDialogOpen}
          onOpenChange={setScoreDialogOpen}
          startupName={selectedStartup?.name}
          drilldown={scoreDrilldown}
          loading={scoreDrilldownLoading}
          error={scoreDrilldownError}
          onRetry={() => selectedStartup && openScoreDrilldown(selectedStartup)}
        />
      </div>
    </MainLayout>
  );
};

export default Dashboard;
